import { useEffect, useMemo, useState } from 'react';
import { Banner, Card, Page, Text, TextField } from '@shopify/polaris';
import { MetricCard } from '../components/MetricCard';
import { MenuSelect } from '../components/MenuSelect';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('uk-UA');
}

const REASON_LABELS: Record<string, string> = {
  active_ready: 'Готово до продажу',
  archived: 'Архів',
  draft_no_media: 'Чернетка без фото',
  draft_no_stock: 'Чернетка без наявності',
  hidden_noncurrent: 'Приховано: немає актуального залишку',
  legacy_unmanaged: 'Поза керуванням 1С',
  legacy_shopify: 'Старий товар Shopify',
  missing_image: 'Немає фото',
  missing_media: 'Немає фото',
  missing_price: 'Немає ціни',
  missing_price_ignored: 'Пропущено: немає ціни',
  no_available_variants: 'Немає доступних розмірів',
  no_media: 'Немає фото',
  no_stock: 'Немає наявності',
  not_in_onec: 'Не знайдено в 1С',
  out_of_stock: 'Немає в наявності',
  unavailable: 'Недоступний',
};

const SHOPIFY_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активний',
  DRAFT: 'Чернетка',
  ARCHIVED: 'Архів',
  active: 'Активний',
  draft: 'Чернетка',
  archived: 'Архів',
};

function fallbackHuman(value?: string | null) {
  return value ? String(value).replace(/[_-]+/g, ' ') : '—';
}

function humanReasonLabel(code?: string | null) {
  return code ? REASON_LABELS[code] || fallbackHuman(code) : '—';
}

function humanShopifyStatus(status?: string | null) {
  return status ? SHOPIFY_STATUS_LABELS[status] || fallbackHuman(status) : '—';
}

function describeCatalogError(message: string) {
  if (/ONEC_BASE_URL|ONEC_API_URL|SHOPIFY_/u.test(message)) {
    return 'Каталог зараз не вдалося перевірити через відсутній доступ до 1С або Shopify у процесі, який збирає звіт.';
  }

  if (/This operation was aborted|AbortError/u.test(message)) {
    return 'Збір каталогу тривав занадто довго. AdminApp має показати останній збережений звіт, а не чекати нову перевірку.';
  }

  if (/Unable to reach onec-worker|fetch failed|ECONNREFUSED|ECONNRESET|ETIMEDOUT/u.test(message)) {
    return 'AdminApp не може отримати звіт від сервера синхронізації. Перевірте, чи працює процес синхронізації 1С.';
  }

  return message;
}

function catalogCardStatus(card: any, report: any) {
  const summary = report?.summary || {};

  if (report?.status === 'pending') return 'pending';
  if (report?.status === 'error') return 'error';

  if (card?.key === 'current_coverage') {
    return Number(summary.currentArticlesMissingInShopifyCount || summary.missingInShopifyCount || 0) > 0 ? 'warning' : 'ok';
  }

  return card?.status || report?.status || 'ok';
}

function presentCatalogMetricSummary(card: any, report: any) {
  const summary = report?.summary || {};

  if (card?.key === 'current_coverage') {
    const missingCount = Number(summary.currentArticlesMissingInShopifyCount || summary.missingInShopifyCount || 0);
    return missingCount > 0
      ? `${missingCount} артикулів з актуального каталогу 1С ще не з’явилися на сайті.`
      : 'Усі актуальні артикули з 1С уже присутні на сайті.';
  }

  if (card?.key === 'draft_products') {
    const draftCount = Number(summary.draftProductCount || 0);
    return draftCount > 0
      ? `${draftCount} товарів ще не готові до публікації: зазвичай бракує фото або наявності.`
      : 'Товарів, які застрягли в чернетках, зараз немає.';
  }

  if (card?.key === 'hidden_unavailable_products') {
    const hiddenCount = Number(summary.hiddenUnavailableProductCount || 0);
    return hiddenCount > 0
      ? `${hiddenCount} товарів приховано, бо зараз немає доступних розмірів для продажу.`
      : 'Немає товарів, які довелося приховати через недоступність.';
  }

  if (card?.key === 'ignored_missing_price_variants') {
    const missingPriceCount = Number(summary.ignoredMissingPriceVariantCount || 0);
    return missingPriceCount > 0
      ? `${missingPriceCount} варіантів не показуються, бо 1С не передала для них ціну.`
      : 'Немає варіантів, які довелося пропустити через відсутню ціну.';
  }

  if (card?.key === 'legacy_unmanaged_products') {
    const legacyCount = Number(summary.legacyUnmanagedProductCount || 0);
    return legacyCount > 0
      ? `${legacyCount} товарів лишаються на сайті поза керуванням 1С.`
      : 'Усі товари на сайті вже входять у керований контур 1С.';
  }

  return card?.summary || '';
}

function buildCatalogAttentionItems(report: any) {
  const summary = report?.summary || {};
  const items = [];

  const missingCount = Number(summary.currentArticlesMissingInShopifyCount || summary.missingInShopifyCount || 0);
  if (missingCount > 0) {
    items.push(`У 1С є ${missingCount} актуальних артикулів, яких ще немає на сайті.`);
  }

  return items;
}

export function CatalogPage({ api, searchValue }: any) {
  const [report, setReport] = useState<any>(null);
  const [error, setError] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);

  const refresh = async (options: { manual?: boolean } = {}) => {
    if (options.manual) {
      setRefreshing(true);
      setRefreshMessage('');
    }

    try {
      const nextReport = await api.getCatalogHealth();
      setReport(nextReport);
      setLastRefreshedAt(new Date().toISOString());
      setError(nextReport?.status === 'error' ? String(nextReport?.message || 'Не вдалося зібрати звіт каталогу') : '');
      if (options.manual && nextReport?.status !== 'error') {
        setRefreshMessage('Стан каталогу оновлено.');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося зібрати звіт каталогу');
      if (options.manual) {
        setRefreshMessage('');
      }
    } finally {
      if (options.manual) {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filteredRows = useMemo(() => {
    const normalizedQuery = String(searchValue || '').trim().toLowerCase();
    return (report?.rows || []).filter((row: any) => {
      if (reasonFilter !== 'all' && row.reasonCode !== reasonFilter) return false;
      if (statusFilter !== 'all' && row.shopifyStatus !== statusFilter) return false;
      if (!normalizedQuery) return true;
      return [
        row.article,
        row.productTitle,
        row.reasonLabel,
        humanReasonLabel(row.reasonCode),
        humanShopifyStatus(row.shopifyStatus),
      ].join(' ').toLowerCase().includes(normalizedQuery);
    });
  }, [reasonFilter, report?.rows, searchValue, statusFilter]);

  const reportMeta = report?.meta || {};
  const showPendingBanner = report?.status === 'pending';
  const showStaleBanner = reportMeta.snapshotState === 'stale';
  const attentionItems = useMemo(() => buildCatalogAttentionItems(report), [report]);

  return (
    <Page
      title="Каталог"
      subtitle="Що вже на сайті, що стримується і з якої причини"
      primaryAction={{
        content: 'Оновити',
        onAction: () => { void refresh({ manual: true }); },
        loading: refreshing,
      }}
    >
      <div className="admin-stack">
        {showPendingBanner ? (
          <Banner tone="info" title="Звіт каталогу ще готується">
            <p>{report?.message || 'Сервер синхронізації збирає перший звіт. Оновіть сторінку трохи пізніше.'}</p>
          </Banner>
        ) : null}

        {showStaleBanner ? (
          <Banner tone="warning" title="Показано останній збережений звіт">
            <p>Новий звіт оновлюється у фоні. Поточні дані ще придатні для перегляду.</p>
          </Banner>
        ) : null}

        {attentionItems.length > 0 ? (
          <Banner tone="warning" title="Каталог потребує уваги">
            <ul className="admin-list">
              {attentionItems.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </Banner>
        ) : null}

        {refreshMessage ? (
          <Banner tone="success" title="Каталог оновлено">
            <p>{refreshMessage}</p>
          </Banner>
        ) : null}

        {error ? (
          <Banner tone="critical" title="Стан каталогу не вдалося зібрати">
            <p>{describeCatalogError(error)}</p>
            <details className="admin-advanced-details">
              <summary>Технічна причина</summary>
              <Text as="p" variant="bodySm" tone="subdued">{error}</Text>
            </details>
          </Banner>
        ) : null}

        <div className="admin-grid admin-grid--metrics">
          {(report?.cards || []).map((card: any) => (
            <MetricCard
              key={card.key}
              label={card.label}
              value={card.value}
              summary={presentCatalogMetricSummary(card, report)}
              status={catalogCardStatus(card, report)}
            />
          ))}
        </div>

        <Card>
          <div className="admin-section-head">
            <Text as="h2" variant="headingMd">Фільтри</Text>
            <Text as="p" variant="bodySm" tone="subdued">Оновлено: {formatDateTime(report?.generatedAt || lastRefreshedAt)}</Text>
          </div>
          <div className="admin-filter-grid">
            <MenuSelect
              label="Причина"
              options={[{ label: 'Усі', value: 'all' }, ...((report?.filters?.reasonCodes || []).map((code: string) => ({ label: humanReasonLabel(code), value: code })))]}
              value={reasonFilter}
              onChange={setReasonFilter}
            />
            <MenuSelect
              label="Статус Shopify"
              options={[{ label: 'Усі', value: 'all' }, ...((report?.filters?.shopifyStatuses || []).map((status: string) => ({ label: humanShopifyStatus(status), value: status })))]}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <TextField autoComplete="off" label="Пошук" value={String(searchValue || '')} onChange={() => {}} disabled helpText="Використовується глобальний пошук у top bar." />
          </div>
        </Card>

        <Card>
          <div className="admin-stack">
            <Text as="h2" variant="headingMd">Стан товарів і варіантів</Text>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Артикул</th>
                    <th>Назва</th>
                    <th>Статус</th>
                    <th>Причина</th>
                    <th>Останнє підтвердження з 1С</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row: any) => (
                    <tr key={row.id}>
                      <td>{row.article || '—'}</td>
                      <td>{row.productTitle || '—'}</td>
                      <td>{humanShopifyStatus(row.shopifyStatus)}</td>
                      <td>{row.reasonLabel || humanReasonLabel(row.reasonCode)}</td>
                      <td>{formatDateTime(row.lastSeenInOnec)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
